import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import API from '../api'; // Configura l'oggetto Axios

const SearchBar = () => {
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tutti');
  const [suggestions, setSuggestions] = useState({ categories: [], products: [], vendors: [] });
  const navigate = useNavigate();
  const location = useLocation(); // aggiunto
  const searchBarRef = useRef(null); // Per rilevare i clic esterni
  
  // Fetch categorie padre (root) all'avvio
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await API.get('/categories/root');
        setCategories(res.data || []);
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Fetch risultati unificati (suggestion + preview)
  useEffect(() => {
    const fetchQuickResults = async () => {
      if (query.trim()) {
        try {
          const res = await API.get('/search/preview', {
            params: {
              query,
              category: selectedCategory !== 'Tutti' ? selectedCategory : undefined,
            },
          });
          setSuggestions(res.data.results || { categories: [], products: [], vendors: [] });
        } catch (error) {
          console.error('Errore durante il fetch dei risultati:', error);
        }
      } else {
        setSuggestions({ categories: [], products: [], vendors: [] });
      }
    };
    fetchQuickResults();
  }, [query, selectedCategory]);

  // Sincronizza stato con URL (query e categoria)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('query') || '';
    const urlCategory = params.get('category') || 'Tutti';
    setQuery(urlQuery);
    setSelectedCategory(urlCategory);
  }, [location.search]);

  const closeDropdowns = () => {
    setSuggestions({ categories: [], products: [], vendors: [] });
  };
  
  // Aggiungi event listener per chiudere quando clicchi fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        closeDropdowns(); // Chiude i dropdown
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Funzione per la ricerca completa
  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?query=${encodeURIComponent(query.trim())}&category=${encodeURIComponent(selectedCategory)}`);
      closeDropdowns(); // Chiude i dropdown
    }
  };

  // Resetta la barra di ricerca
  const handleReset = () => {
    setQuery('');
    closeDropdowns();
  };

  // Naviga a un prodotto selezionato
  const handleSelectResult = (result) => {
    navigate(`/products/${result.id}`);
    closeDropdowns(); // Chiude i dropdown
  };

  // Seleziona un suggerimento
  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    closeDropdowns(); // Chiude i suggerimenti
  };

  // Handler per chiudere i suggerimenti quando si clicca su un link
  const handleSuggestionClick = () => {
    closeDropdowns();
  };

  return (
    <div className="searchbar-container" ref={searchBarRef}>
      <form className="searchbar-form" onSubmit={handleSearch}>
        {/* Dropdown categoria */}
        <select
          className="searchbar-category"
          value={selectedCategory}
          onChange={(e) => {
            const newCategory = e.target.value;
            setSelectedCategory(newCategory);
            // Se siamo già in una pagina di ricerca, naviga immediatamente
            if (location.pathname === '/search' && query.trim()) {
              navigate(`/search?query=${encodeURIComponent(query.trim())}&category=${encodeURIComponent(newCategory)}`);
            }
          }}
        >
          <option value="Tutti">Tutti</option>
          {categories.map((category) => (
            <option key={category.category_handle} value={category.category_handle}>
              {category.category_title}
            </option>
          ))}
        </select>

        {/* Input barra di ricerca */}
        <div className="searchbar-input-wrapper">
          <input
            type="text"
            className="searchbar-input"
            placeholder="Cerca un prodotto e compara i prezzi..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="searchbar-reset" onClick={handleReset}>
              ✕
            </button>
          )}
        </div>

        {/* Pulsante cerca */}
        <button type="submit" className="searchbar-submit" aria-label="Cerca">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="searchbar-submit-icon">
            <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="2" />
            <line x1="15.2" y1="15.2" x2="18" y2="18" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </form>

      {/* Suggerimenti + Preview Unificati */}
      {(suggestions.categories?.length > 0 || suggestions.products?.length > 0 || suggestions.vendors?.length > 0) && (
        <div className="searchbar-suggestions">
          {/* Categorie */}
          {suggestions.categories?.length > 0 && (
            <>
              <div className="searchbar-suggestions-title">Categorie</div>
              <ul className="searchbar-suggestions-list">
                {suggestions.categories.map((cat, index) => (
                  <li key={cat.category_handle || index} className="searchbar-suggestion-item">
                    <Link to={`/categories/${cat.category_handle}`} className="searchbar-suggestion-link" onClick={handleSuggestionClick}>
                      {cat.category_title}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {/* Prodotti */}
          {suggestions.products?.length > 0 && (
            <>
              <div className="searchbar-suggestions-title">Prodotti</div>
              <div className="searchbar-suggestions-products-grid">
                {/* Colonna 1 (prodotti 0-4) */}
                <ul className="searchbar-suggestions-list">
                  {suggestions.products.slice(0, 5).map((prod, index) => (
                    <li key={prod.id || prod.title + index} className="searchbar-suggestion-item">
                      <Link to={`/products/${prod.id}`} className="searchbar-suggestion-link" onClick={handleSuggestionClick}>
                        {prod.main_image_url && (
                          <img src={prod.main_image_url} alt={prod.title} className="searchbar-suggestion-product-img" />
                        )}
                        <div className="searchbar-suggestion-product-info">
                          <span className="searchbar-product-title">{prod.title}</span>
                          {typeof prod.price_min !== 'undefined' && (
                            <span className="searchbar-suggestion-product-price">{Number(prod.price_min).toFixed(2)} €</span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                {/* Colonna 2 (prodotti 5-9) */}
                <ul className="searchbar-suggestions-list">
                  {suggestions.products.slice(5, 10).map((prod, index) => (
                    <li key={prod.id || prod.title + (index + 5)} className="searchbar-suggestion-item">
                      <Link to={`/products/${prod.id}`} className="searchbar-suggestion-link" onClick={handleSuggestionClick}>
                        {prod.main_image_url && (
                          <img src={prod.main_image_url} alt={prod.title} className="searchbar-suggestion-product-img" />
                        )}
                        <div className="searchbar-suggestion-product-info">
                          <span className="searchbar-product-title">{prod.title}</span>
                          {typeof prod.price_min !== 'undefined' && (
                            <span className="searchbar-suggestion-product-price">{Number(prod.price_min).toFixed(2)} €</span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {/* Negozi */}
          {suggestions.vendors?.length > 0 && (
            <>
              <div className="searchbar-suggestions-title">Negozi</div>
              <ul className="searchbar-suggestions-list">
                {suggestions.vendors.map((vendor, index) => (
                  <li key={vendor.seller_handle || vendor.id || index} className="searchbar-suggestion-item">
                    <Link to={`/seller/${vendor.seller_handle}`} className="searchbar-suggestion-link" onClick={handleSuggestionClick}>
                      {vendor.store_logo_square && (
                        <img src={vendor.store_logo_square} alt={vendor.store_name} className="searchbar-seller-logo" />
                      )}
                      {vendor.store_name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
